import { useState, useEffect, useContext, useRef } from 'react'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import api from '../api';
import "../styles/gradientButton.css"
import scrollContext from '../contexts/scrollContext'
import Reordering from '../components/Reordering';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { buildListPath, toShortOrdering, toFullOrdering, DEFAULT_SORT } from '../lib/urls';
import { FaFilter } from "react-icons/fa6";
import UseDebounce from '../hooks/useDebounce'
import '../styles/changeButton.css'
import HouseCard from '../components/HouseCard';
import SeoText from '../components/SeoText';
import initialDataContext from '../contexts/initialDataContext';
import { breadcrumbSchema, itemListSchema, categoryName } from '../lib/jsonLd';
import { getListMetaDescription } from '../lib/metaDescriptions';
import { ruListPath, ruUrl, RU_BASE_MAP, ruH1, RU_LIST_META, listPathLang, ruItemListSchema } from '../lib/ru';
import { useTr } from '../lib/i18n';
import HouseFiltersModal from '../components/HouseFiltersModal';
import { TbMapPinSearch } from "react-icons/tb";
import { Helmet } from 'react-helmet-async';

const Houses = ({ locationSlug = '', sectorSlug = '', saleType: saleTypeProp = 1, saleTypeInPath = false }) => {
    const hasMounted = useRef(false);

    // Data pre-fetched by the Express server during SSR.
    const ssrData = useContext(initialDataContext);
    const ssr = (ssrData && ssrData.type === 'list' && ssrData.base === 'case' && ssrData.locationSlug === locationSlug && ssrData.sectorSlug === sectorSlug && Number(ssrData.saleType) === Number(saleTypeProp)) ? ssrData : null;
    const tr = useTr();
    const lang = tr('ro', 'ru');

    const [houses, setHouses] = useState(() => ssr?.results ?? [])
    const [show, setShow] = useState(() => !!ssr);
    const [loading, setLoading] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const { enableScroll, disableScroll } = useContext(scrollContext)
    const navigate = useNavigate();

    const [locations, setLocations] = useState(() => ssr?.options?.['locations'] ?? []);
    const [sectors, setSectors] = useState(() => ssr?.options?.['sectors'] ?? []);
    const [saleTypes, setSaleTypes] = useState(() => ssr?.options?.['sale-types'] ?? []);

    const [searchParams, setSearchParams] = useSearchParams();
    const currentPageFromUrl = parseInt(searchParams.get('page')) || 1;
    const initialOrdering = toShortOrdering(searchParams.get('sort') ?? searchParams.get('ordering') ?? DEFAULT_SORT);

    const [ordering, setOrdering] = useState(initialOrdering);

    const toBool = (v) => v === 'true' ? true : v === 'false' ? false : '';
    const toIntOrEmpty = (v) => {
        const num = parseInt(v);
        return isNaN(num) ? '' : num;
    };

    const [minSurface, setMinSurface] = useState(() => searchParams.get('min_surface') || '');
    const [maxSurface, setMaxSurface] = useState(() => searchParams.get('max_surface') || '');
    const [minLandSurface, setMinLandSurface] = useState(() => searchParams.get('min_land_surface') || '');
    const [maxLandSurface, setMaxLandSurface] = useState(() => searchParams.get('max_land_surface') || '');
    const [minRooms, setMinRooms] = useState(() => toIntOrEmpty(searchParams.get('min_rooms')));
    const [maxRooms, setMaxRooms] = useState(() => toIntOrEmpty(searchParams.get('max_rooms')));
    const [floors, setFloors] = useState(() => searchParams.get('total_floors') || '');
    const [bathrooms, setBathrooms] = useState(() => searchParams.get('bathrooms') || '');
    const [hasWater, setHasWater] = useState(() => toBool(searchParams.get('water_installation')));
    const [hasGas, setHasGas] = useState(() => toBool(searchParams.get('gas_installation')));
    const [hasSewerage, setHasSewerage] = useState(() => toBool(searchParams.get('sewerage_installation')));
    const [minPrice, setMinPrice] = useState(() => searchParams.get('price_min') || '');
    const [maxPrice, setMaxPrice] = useState(() => searchParams.get('price_max') || '');
    const [location, setLocation] = useState(() => locationSlug || searchParams.get('location') || '');
    const [sector, setSector] = useState(() => sectorSlug || searchParams.get('sector') || '');
    const [saleType, setSaleType] = useState(() => saleTypeProp !== 1 ? saleTypeProp : (parseInt(searchParams.get('sale_type')) || 1));

    // Sync state when the route changes (e.g. /case/balti -> /case/ungheni).
    useEffect(() => {
        setLocation(locationSlug || searchParams.get('location') || '');
        setSector(sectorSlug || searchParams.get('sector') || '');
    }, [locationSlug, sectorSlug]);

    useEffect(() => {
        setSaleType(saleTypeProp !== 1 ? saleTypeProp : (parseInt(searchParams.get('sale_type')) || 1));
    }, [saleTypeProp]);

    // Location/sector pickers update the path (clean SEO URL).
    const locSlug = (v) => locations.find(l => l.id === Number(v))?.slug ?? v;

    const handleSetLocation = (value) => {
        setLocation(value);
        setSector('');
        navigate(listPathLang(lang, 'case', { location: locSlug(value), sector: '', saleType }));
    };

    const handleSetSector = (value) => {
        setSector(value);
        navigate(listPathLang(lang, 'case', { location: locSlug(location) || 'chisinau', sector: value, saleType }));
    };

    const debouncedMinPrice = UseDebounce(minPrice, 500);
    const debouncedMaxPrice = UseDebounce(maxPrice, 500);
    const debouncedMinSurface = UseDebounce(minSurface, 500);
    const debouncedMaxSurface = UseDebounce(maxSurface, 500);
    const debouncedMinLandSurface = UseDebounce(minLandSurface, 500);
    const debouncedMaxLandSurface = UseDebounce(maxLandSurface, 500);
    const debouncedFloors = UseDebounce(floors, 500);
    const debouncedBathrooms = UseDebounce(bathrooms, 500);

    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const [pagination, setPagination] = useState(() => ssr
        ? { count: ssr.count, next: null, previous: null, current: currentPageFromUrl }
        : { count: 0, next: null, previous: null, current: currentPageFromUrl });

    useEffect(() => {
        if (!showFilterModal) {
            enableScroll()
        } else {
            disableScroll()
        }
    }, [showFilterModal])

    function cleanAndBuildUrl(baseUrl, allParams) {
        const searchParams = new URLSearchParams();

        for (const [key, value] of Object.entries(allParams)) {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        }

        return `${baseUrl}?${searchParams.toString()}`;
    }

    const fetchHouses = async (page = 1, sort = ordering, append = false) => {
        if (!append) setLoading(true);
        if (append) setLoadingMore(true);
        try {
            const filters = {
                page: page.toString(),
                ordering: toFullOrdering(sort),
                sale_type: saleType,
                availability: true,
                min_surface: debouncedMinSurface,
                max_surface: debouncedMaxSurface,
                min_land_surface: debouncedMinLandSurface,
                max_land_surface: debouncedMaxLandSurface,
                min_rooms: minRooms,
                max_rooms: maxRooms,
                total_floors: debouncedFloors,
                bathrooms: debouncedBathrooms,
                water_installation: hasWater,
                gas_installation: hasGas,
                sewerage_installation: hasSewerage,
                price_min: debouncedMinPrice,
                price_max: debouncedMaxPrice,
                location,
                sector,
            };

            const url = cleanAndBuildUrl('/api/v1/houses/', filters);

            const [housesRes] = await Promise.all([
                api.get(`${url}`)
            ]);

            if (housesRes.status === 200) {
                if (append) {
                    setHouses(prev => [...prev, ...housesRes.data.results]);
                } else {
                    setHouses(housesRes.data.results);
                }
                setPagination({
                    count: housesRes.data.count,
                    next: housesRes.data.next,
                    previous: housesRes.data.previous,
                    current: page,
                });
                if (append) setHasMore(housesRes.data.results.length > 0);
                else setHasMore(true);

                // Write only non-default values to the URL — clean SEO URLs.
                const next = {};
                const shortSort = toShortOrdering(sort);
                if (shortSort !== DEFAULT_SORT) next.sort = shortSort;
                if (!locationSlug && location) next.location = location;
                if (!sectorSlug && sector) next.sector = sector;
                if (!saleTypeInPath && saleType !== 1) next.sale_type = String(saleType);
                if (minSurface !== '') next.min_surface = minSurface;
                if (maxSurface !== '') next.max_surface = maxSurface;
                if (minLandSurface !== '') next.min_land_surface = minLandSurface;
                if (maxLandSurface !== '') next.max_land_surface = maxLandSurface;
                if (minRooms !== '') next.min_rooms = String(minRooms);
                if (maxRooms !== '') next.max_rooms = String(maxRooms);
                if (floors !== '') next.total_floors = floors;
                if (bathrooms !== '') next.bathrooms = bathrooms;
                if (hasWater !== '') next.water_installation = String(hasWater);
                if (hasGas !== '') next.gas_installation = String(hasGas);
                if (hasSewerage !== '') next.sewerage_installation = String(hasSewerage);
                if (minPrice !== '') next.price_min = minPrice;
                if (maxPrice !== '') next.price_max = maxPrice;
                if (!append) setSearchParams(next);
                if (!append) window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }

            setTimeout(() => { setLoading(false); setLoadingMore(false); }, 500);
        } catch (error) {
            toast.error(tr('Nu s-a putut încărca informația, încercați mai târziu.', 'Не удалось загрузить данные. Попробуйте позже.'));
            setLoading(false); setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchHouses(1, ordering,);
    }, [
        debouncedMinSurface,
        debouncedMaxSurface,
        debouncedMinLandSurface,
        debouncedMaxLandSurface,
        minRooms,
        maxRooms,
        debouncedFloors,
        debouncedBathrooms,
        debouncedMinPrice,
        debouncedMaxPrice,
        hasWater,
        hasGas,
        hasSewerage,
        location,
        sector,
        saleType,
        ordering
    ]);

    useEffect(() => {
        if (ssr) {
            hasMounted.current = true;
            return;
        }
        fetchHouses(currentPageFromUrl, ordering);
        hasMounted.current = true;
    }, []);

    useEffect(() => {
        // SSR already delivered the data — skip the option fetch on mount.
        if (ssr) {
            setShow(true);
            return;
        }
        const fetchAllData = async () => {
            try {
                const [locationsRes, sectorsRes, saleTypesRes] = await Promise.all([
                    api.get('/api/v1/locations/'),
                    api.get('/api/v1/sectors/'),
                    api.get('/api/v1/sale-types/')
                ]);

                if (
                    locationsRes.status === 200 &&
                    sectorsRes.status === 200 &&
                    saleTypesRes.status === 200
                ) {
                    setLocations(locationsRes.data);
                    setSectors(sectorsRes.data);
                    setSaleTypes(saleTypesRes.data);
                    // Unknown location/sector slug in path -> 404.
                    if (locationSlug && !locationsRes.data.some(l => l.slug === locationSlug)) {
                        navigate(lang === 'ru' ? '/ru/404' : '/404');
                        return;
                    }
                    if (sectorSlug && !sectorsRes.data.some(s => s.slug === sectorSlug)) {
                        navigate(lang === 'ru' ? '/ru/404' : '/404');
                        return;
                    }
                    setTimeout(() => setShow(true), 500);
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                } else {
                    throw new Error('Eroare');
                }
            } catch (error) {
                toast.error('Nu s-a putut încărca informația, încercați mai târziu.');
                setShow(false);
            }
        };

        fetchAllData();
    }, []);

    const totalPages = Math.ceil(pagination.count / 15);

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const buttons = [];

        const getButton = (page) => (
            <button
                key={page}
                onClick={() => fetchHouses(page)}
                className={`px-3 py-1 bg-[#0f1115] rounded cursor-pointer font-body hover:scale-[1.03] transition-all duration-75 ease-in ${pagination.current === page ? 'bg-[#d5ab63] text-white' : 'text-white'
                    }`}
            >
                {page}
            </button>
        );

        if (pagination.current > 1) {
            buttons.push(
                <button key="first" onClick={() => fetchHouses(1)} className='cursor-pointer hover:scale-[1.03] transition-all duration-75 ease-in'>
                    «
                </button>
            );
            buttons.push(
                <button key="prev" onClick={() => fetchHouses(pagination.current - 1)} className='cursor-pointer hover:scale-[1.03] transition-all duration-75 ease-in'>
                    ‹
                </button>
            );
        }

        let start = pagination.current - 1;
        let end = pagination.current + 1;

        if (pagination.current === 1) {
            start = 1;
            end = Math.min(3, totalPages);
        } else if (pagination.current === totalPages) {
            end = totalPages;
            start = Math.max(1, totalPages - 2);
        }

        for (let i = start; i <= end; i++) {
            buttons.push(getButton(i));
        }

        if (pagination.current < totalPages) {
            buttons.push(
                <button key="next" onClick={() => fetchHouses(pagination.current + 1)} className='cursor-pointer hover:scale-[1.03] transition-all duration-75 ease-in'>
                    ›
                </button>
            );
            buttons.push(
                <button key="last" onClick={() => fetchHouses(totalPages)} className='cursor-pointer hover:scale-[1.03] transition-all duration-75 ease-in'>
                    »
                </button>
            );
        }

        return (
            <div className="flex gap-[10px] mt-[20px] flex-wrap text-[14px] justify-center items-center">
                {buttons}
            </div>
        );
    };

    const saleTypeText = saleTypes.find(t => t.id === saleType)?.type || (saleType === 2 ? 'Chirie' : 'Vânzare');
    const prettySlug = (slug) => slug ? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ') : '';
    const locationText = locations.find(l => l.slug === location || l.id === Number(location))?.location || prettySlug(locationSlug) || 'Moldova';
    const sectorText = sectors.find(s => s.slug === sector || s.id === Number(sector))?.sector || prettySlug(sectorSlug) || '';
    const h1Ro = `Case de ${saleType === 2 ? 'închiriat' : 'vânzare'} în ${sectorText ? `${sectorText}, ` : ''}${locationText}`;
    const h1Text = lang === 'ru' ? ruH1('doma', saleType, locationSlug) : h1Ro;
    const seoLoc = `${sectorText ? `${sectorText}, ` : ''}${locationText}`;
    const title = `${h1Text} | LVG Imob`;

    const breadcrumbItems = [
        { name: tr('Acasă', 'Главная'), item: lang === 'ru' ? 'https://lvgimob.md/ru/' : 'https://lvgimob.md/' },
        { name: lang === 'ru' ? 'Дома' : categoryName('case'), item: lang === 'ru' ? `https://lvgimob.md${ruListPath('doma', locationSlug || 'ungheni', saleType)}` : 'https://lvgimob.md/case' },
        ...(lang === 'ro' && locationSlug ? [{ name: `Case în ${locationText}`, item: `https://lvgimob.md/case/${locationSlug}` }] : []),
        { name: h1Text },
    ];
    const breadcrumbJson = breadcrumbSchema(breadcrumbItems);
    const itemListJson = lang === 'ru' ? ruItemListSchema('doma', locationSlug || 'ungheni', houses) : itemListSchema('case', houses);
    const descriptionRo = getListMetaDescription('case', { locationSlug, sectorSlug, saleType, seoLoc, h1Text });
    const description = lang === 'ru'
        ? ((locationSlug === 'ungheni' && RU_LIST_META[`doma|${saleType}`]) || `${h1Text}. Смотрите актуальные предложения, цены и фотографии недвижимости от LVG Imob.`)
        : descriptionRo;
    const ruListUrl = (lang === 'ru' || (!sectorSlug && ['', 'ungheni', 'chisinau', 'balti'].includes(locationSlug)))
        ? ruUrl(ruListPath('doma', locationSlug, saleType))
        : null;
    const roCanonical = `https://lvgimob.md${buildListPath('case', { location: locationSlug, sector: sectorSlug, saleType })}`;
    const ruCanonical = ruListUrl || roCanonical;

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={lang === 'ru' ? ruCanonical : roCanonical} />
                {houses[0]?.listing?.thumbnails?.[0] && <link rel="preload" as="image" href={houses[0].listing.thumbnails[0]} fetchPriority="high" />}
                <link rel="alternate" hreflang="ro" href={roCanonical} />
                {ruListUrl && <link rel="alternate" hreflang="ru" href={ruListUrl} />}
                <link rel="alternate" hreflang="x-default" href={roCanonical} />
                <script type="application/ld+json">{JSON.stringify(breadcrumbJson)}</script>
                {itemListJson && <script type="application/ld+json">{JSON.stringify(itemListJson)}</script>}
            </Helmet>

            <ToastContainer position="bottom-left" autoClose={5000} />
            <section className='py-[60px] sm:py-[80px] px-[12px] flex flex-col sm:container sm:mx-auto' style={{ minHeight: "calc(100vh - 88px - 561px)" }}>

                <div className="flex flex-row gap-[20px] xl:gap-[50px] justify-between overflow-hidden">

                    <div className="w-full">
                        <div className="flex flex-col gap-[25px]">
                            <h1 className="text-[22px] font-poppins font-[500] sm:text-[24px]">{h1Text}</h1>
                            {(locationSlug || sectorSlug) && (
                                <SeoText part="intro" base="case" saleType={saleType} loc={seoLoc} locationSlug={locationSlug} sectorSlug={sectorSlug} />
                            )}
                            {show && <p className="text-[16px] font-poppins mb-[10px]">{lang === 'ru' ? `Найдено объявлений: ${pagination.count}` : `${pagination.count} rezultate ale căutării`} </p>}
                    {
                        (show) ? (
                            <>
                                        <div className="flex xl:flex-row flex-col gap-4 xl:justify-between mb-4">
                                            <div className="flex xl:hidden gap-[10px] flex-wrap font-body">
                                                <Link to={listPathLang(lang, 'apartamente', { location, sector, saleType })}
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200 `}
                                                >
                                                    {tr('Apartamente', 'Квартиры')}
                                                </Link>
                                                <div
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in bg-[#181616] !border-[#181616] text-white`}
                                                >
                                                    {tr('Case', 'Дома')}
                                                </div>
                                                <Link to={listPathLang(lang, 'terenuri', { location, sector, saleType })}
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                >
                                                    {tr('Terenuri', 'Участки')}
                                                </Link>
                                                <Link to={listPathLang(lang, 'spatii-comerciale', { location, sector, saleType })}
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                >
                                                    {tr('Spații comerciale', 'Коммерческая недвижимость')}
                                                </Link>
                                                       
                                                  <Link to={listPathLang(lang, 'garajuri', { location, sector, saleType })}
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[15px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                >
                                                    {tr('Garajuri', 'Гаражи')}
                                                </Link>
                                                <Link to={listPathLang(lang, 'altele', { location, sector, saleType })}
                                                    style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                    className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[15px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                >
                                                    {tr('Altele', 'Прочее')}
                                                </Link>

                                            </div>

                                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 xl:w-full">
                                                <div className="xl:flex hidden gap-[10px] flex-wrap font-body">
                                                    <Link to={listPathLang(lang, 'apartamente', { location, sector, saleType })}
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200 `}
                                                    >
                                                        {tr('Apartamente', 'Квартиры')}
                                                    </Link>
                                                    <div
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in bg-[#181616] !border-[#181616] text-white`}
                                                    >
                                                        {tr('Case', 'Дома')}
                                                    </div>
                                                    <Link to={listPathLang(lang, 'terenuri', { location, sector, saleType })}
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                    >
                                                        {tr('Terenuri', 'Участки')}
                                                    </Link>
                                                    <Link to={listPathLang(lang, 'spatii-comerciale', { location, sector, saleType })}
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                    >
                                                        {tr('Spații comerciale', 'Коммерческая недвижимость')}
                                                    </Link>
                                                    
                                                      <Link to={listPathLang(lang, 'garajuri', { location, sector, saleType })}
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                    >
                                                        {tr('Garajuri', 'Гаражи')}
                                                    </Link>

                                                      <Link to={listPathLang(lang, 'altele', { location, sector, saleType })}
                                                        style={{ flexBasis: 'auto', flexGrow: 1 }}
                                                        className={`border-[1px] border-gray-300 font-body text-center rounded-[5px] px-[12px] py-[10.5px] cursor-pointer transition-all duration-100 ease-in hover:bg-gray-200`}
                                                    >
                                                        {tr('Altele', 'Прочее')}
                                                    </Link>
                                                </div>

                                                <div className="flex items-center mx-auto xs:mx-0">
                                                    <div style={{ display: "flex" }}>
    <div className="mask-box">

                                                            <div
        className="mask"
        style={{
            transform: `translateX(${saleType === 2 ? "100px" : "0px"})`
        }}
    />
                                                            {saleTypes.map((type) => (
                                                                <button
                                                                    key={type.id}
                                                                    type="button"
                                                                                                                                        style={{
                                                                        color: saleType === type.id ? "#ffffff" : "#0f1115",
                                                                        fontFamily: "Poppins, sans-serif",
                                                                        textTransform: "none"
                                                                    }}
                                                                    onClick={() => {
                                                                        setSaleType(type.id);
                                                                        navigate(listPathLang(lang, 'case', { location: locSlug(location), sector, saleType: type.id }));
                                                                    }}
                                                                >
                                                                    {lang === 'ru' ? (type.id === 2 ? 'Аренда' : 'Продажа') : type.type}
                                                                </button>
                                                            ))}
    </div>
</div>
                                                </div>

                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    <select
                                                        aria-label={tr('Sortează anunțurile', 'Сортировать объявления')}
className="font-body px-[16px] py-[10px] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] min-w-[250px]"
                                                        onChange={(e) => setOrdering(e.target.value)}
                                                        value={ordering}
                                                    >
                                                        <option value="pret">{tr('Ieftin - Scump', 'Сначала дешевле')}</option>
                                                        <option value="-pret">{tr('Scump - Ieftin', 'Сначала дороже')}</option>
                                                        <option value="noi">{tr('Cele mai noi', 'Новые')}</option>
                                                    </select>

                                                    <button
                                                        onClick={() => setShowFilterModal(true)}
                                                        className="bg-[#0f1115] justify-center font-body flex gap-[10px] items-center text-white px-[16px] py-[10px] border-[1px] border-[#0f1115] rounded-[5px] cursor-pointer hover:bg-[#d5ab63] hover:border-[#d5ab63] transition-all duration-75 ease-in"
                                                    >
                                                        <FaFilter />
                                                        <p>{tr('Filtre', 'Фильтры')}</p>
                                                    </button>
                                                </div>

                                            </div>
                                        </div>
                                    {
                                        loading ? (
                                            <div className="flex justify-center items-center w-full mt-[50px]">
                                                <Reordering />
                                            </div>
                                        ) : (
                                            <>
                                                {
                                                    houses.length === 0 && (
                                                        <div className="flex flex-col gap-[15px] items-center w-full h-full mt-[80px] text-center">
                                                            <TbMapPinSearch className='text-[#d5ab63] text-[90px]' />
                                                            <p className='text-[18px] font-poppins font-[500]'>{lang === 'ru' ? `Пока нет объявлений: ${h1Text} на LVG Imob` : `Nu avem încă listate ${h1Text} la LVG Imob`}</p>
                                                            <p className='text-[15px] font-body text-gray-700 max-w-[600px]'>{tr('Putem face o căutare pentru tine și te contactăm când apare o ofertă potrivită.', 'Мы подберём для вас вариант и сообщим, как только появится подходящее предложение.')}</p>
                                                            <Link to={lang === 'ru' ? '/ru/contacte' : '/contacte'} className='btn-main text-white text-[14px] font-body px-[28px] py-[10px] rounded-[5px] w-fit'>{tr('Lasă o cerere', 'Оставьте заявку')}</Link>
                                                            <p className='text-[15px] font-body text-gray-700 max-w-[600px]'>{lang === 'ru' ? `Вы собственник и предлагаете ${h1Text}?` : `Ești proprietar și oferi ${h1Text}?`}</p>
                                                            <Link to="/cerere" className='btn-main text-white text-[14px] font-body px-[28px] py-[10px] rounded-[5px] w-fit'>{tr('Anunță-ne', 'Сообщите нам')}</Link>
                                                            {(minPrice || maxPrice || minSurface || maxSurface || minLandSurface || maxLandSurface || minRooms || maxRooms || floors || bathrooms || hasWater !== '' || hasGas !== '' || hasSewerage !== '') && (
                                                                <button onClick={() => {
                                                                    setMinSurface('');
                                                                    setMaxSurface('');
                                                                    setMinPrice('');
                                                                    setMaxPrice('');
                                                                    setLocation('');
                                                                    setSector('');
                                                                    setMinLandSurface('');
                                                                    setMaxLandSurface('');
                                                                    setMinRooms('');
                                                                    setMaxRooms('');
                                                                    setFloors('');
                                                                    setBathrooms('');
                                                                    setHasWater('');
                                                                    setHasGas('');
                                                                    setHasSewerage('');
                                                                    navigate(listPathLang(lang, 'case', { location: '', sector: '', saleType }));
                                                                }} className='btn-main text-[14px] text-white px-[30px] py-[7px] cursor-pointer rounded-[5px] font-poppins'>{tr('Șterge filtrele', 'Сбросить фильтры')}</button>
                                                            )}
                                                        </div>
                                                    )}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[30px] text-black">
                                                    {houses.map((item, idx) => (
                                                        <HouseCard key={idx} listing={item} />
                                                    ))}
                                                </div>
                                                {(hasMore && pagination.count > houses.length) && (
                                                <div className="flex justify-center mt-[20px]">
                                                    <button onClick={() => fetchHouses(pagination.current + 1, ordering, true)} disabled={loadingMore} className='btn-main text-[14px] text-white px-[40px] py-[10px] cursor-pointer rounded-[5px] font-poppins disabled:opacity-60'>{loadingMore ? tr('Se încarcă...', 'Загрузка...') : tr('Încarcă mai multe', 'Показать ещё')}</button>
                                                </div>
                                            )}
                                            </>
                                        )
                                    }
                                    {
                                        showFilterModal && (
                                            <HouseFiltersModal
                                                onClose={() => setShowFilterModal(false)}
                                                locations={locations}
                                                sectors={sectors}

                                                location={location}
                                                setLocation={handleSetLocation}
                                                sector={sector}
                                                setSector={handleSetSector}
                                                minPrice={minPrice}
                                                setMinPrice={setMinPrice}
                                                maxPrice={maxPrice}
                                                setMaxPrice={setMaxPrice}

                                                minSurface={minSurface}
                                                setMinSurface={setMinSurface}
                                                maxSurface={maxSurface}
                                                setMaxSurface={setMaxSurface}
                                                minLandSurface={minLandSurface}
                                                setMinLandSurface={setMinLandSurface}
                                                maxLandSurface={maxLandSurface}
                                                setMaxLandSurface={setMaxLandSurface}
                                                minRooms={minRooms}
                                                setMinRooms={setMinRooms}
                                                maxRooms={maxRooms}
                                                setMaxRooms={setMaxRooms}
                                                floors={floors}
                                                setFloors={setFloors}
                                                bathrooms={bathrooms}
                                                setBathrooms={setBathrooms}
                                                hasWater={hasWater}
                                                setHasWater={setHasWater}
                                                hasGas={hasGas}
                                                setHasGas={setHasGas}
                                                hasSewerage={hasSewerage}
                                                setHasSewerage={setHasSewerage}
                                            />
                                        )}
                            </>
                        ) : (
                            <div className="w-full justify-center items-center flex mt-[50px] xl:mt-0">
                                <Reordering />
                            </div>
                        )
                    }
                            {(locationSlug || sectorSlug) && (
                                <SeoText part="full" base="case" saleType={saleType} loc={seoLoc} locationSlug={locationSlug} sectorSlug={sectorSlug} />
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default Houses
